import type { Account } from "@/model/app/account"
import type { DebitorProfile } from "@/model/app/debitor-profile"
import type { OrganizationMember } from "@/model/app/organization-member"
import type { CartItem } from "@/model/order/cart-item"
import type { CartItemAssignment } from "@/model/order/cart-item-assignment"
import type { Product } from "@/model/order/product"
import type { PurchasableItem } from "@/model/order/purchasable-item"
import type { ShopOrder } from "@/model/order/shop-order"
import type { PaymentMethod } from "@/model/payment/payment-method"
import { useHttpClient, useHttpGet } from "@/vf"
import { watchDebounced } from "@vueuse/core"
import { computed, ref, type ComputedRef, type Ref } from "vue"
import { useShopPricing } from "./useShopPricing"

export interface UseShopOptions {
    shopOrderId: string
    shopConfigurationId: string
    issueOwnerId?: string | null
}

export interface ShopOrderSubmitPayload {
    debitorProfile: string | undefined
    items: Array<{
        purchasableItem: string | undefined
        amount: number | undefined
        assignments: Array<{
            position: number | undefined
            account: string | null
            assignedName: string | null
        }>
    }>
}

export interface UseShopCustomer {
    notRegisteredForChampionship: boolean
    noMembersFound: boolean
    hasActiveFrameworkAgreement: boolean
    assignableMembers: Ref<OrganizationMember[]>
    getAssignableMembers(product: Product): OrganizationMember[]
}

export interface UseShopCart {
    items: Ref<Map<string, CartItem>>
    total: ComputedRef<number>
    selectedDebitorProfile: Ref<string | undefined>
    /** Empty when the buyer does not have to pick a payment method for this cart. */
    allowedPaymentMethods: Ref<PaymentMethod[]>
    selectedPaymentMethod: Ref<PaymentMethod | null>
    canOrder: ComputedRef<boolean>
    getItemQuantity(itemId: string): number
    getProductTotalQuantity(product: Product): number
    getBestPrice(item: PurchasableItem, amount?: number): number | null
    getMaxOrderQuantity(product: Product, item?: PurchasableItem): number | undefined
    getAssignment(itemId: string, position: number): CartItemAssignment | undefined
    setAssignment(itemId: string, position: number, value: string | Account): void
    updateQuantity(product: Product, item: PurchasableItem, quantity: number): void
    buildPayload(): ShopOrderSubmitPayload
}

export async function useShop(options: UseShopOptions): Promise<{
    shopOrder: Ref<ShopOrder>
    products: Ref<Product[]>
    productItems: (product: Product) => PurchasableItem[]
    debitorProfiles: DebitorProfile[] | null
    customer: UseShopCustomer
    cart: UseShopCart
    availableStock: (item: PurchasableItem) => number | null
    refreshProducts: () => Promise<void>
}> {
    const { issueOwnerId, shopConfigurationId, shopOrderId } = options

    const http = useHttpClient()

    const { data: shopOrderData } = await http.get<ShopOrder>(`/order/shop/${shopConfigurationId}/${shopOrderId}`)
    const shopOrderItem = ref(shopOrderData)
    const shopOrder = shopOrderItem.value
    const organizationId = shopOrder.organization?.id
    const { getBestPrice } = useShopPricing(organizationId)

    const productsUrl = `/order/shop/${shopOrder.shopConfiguration?.listId}/products?itemsPerPage=-1&shopConfiguration=${shopConfigurationId}`

    const { data: productsData, refresh: refreshProductsData } = await useHttpGet<{ list: Product[] }>(productsUrl)
    const products = computed(() => productsData.value?.list ?? [])

    const allPurchasableItems = computed(() =>
        products.value.flatMap(p => (p.variants?.length ? p.variants : [p as PurchasableItem])),
    )

    let debitorProfiles: DebitorProfile[] | null = null
    if (organizationId) {
        const { data } = await http.get<{ list: DebitorProfile[] }>(
            `/organization/${organizationId}/debitor-profile/`,
            { itemsPerPage: "-1" },
        )
        debitorProfiles = data.list
    }

    const assignableMembers = ref<OrganizationMember[]>([])
    let notRegisteredForChampionship = false
    let noMembersFound = false

    if (
        organizationId &&
        products.value.some(
            (product: Product) => product.isUserAssignmentRequired && product.isNominalRegistrationRequired,
        )
    ) {
        const { data: assignableMembersData } = await http.get<{
            hasRegistration: boolean
            members: OrganizationMember[]
        }>(`/order/shop/${shopConfigurationId}/assignable-members`, { organization: organizationId })
        assignableMembers.value = assignableMembersData.members ?? []
        if (!assignableMembersData.hasRegistration) {
            notRegisteredForChampionship = true
        } else if (assignableMembers.value.length === 0) {
            noMembersFound = true
        }
    }

    let hasActiveFrameworkAgreement = false
    if (!organizationId && issueOwnerId && products.value.some(p => p.isFrameworkAgreementRequired)) {
        const { data } = await http.get<{ frameworkAgreement?: { isActive?: boolean } }>(
            `/person-action/account/${issueOwnerId}`,
        )
        hasActiveFrameworkAgreement = data.frameworkAgreement?.isActive ?? false
    }

    function availableStock(item: PurchasableItem): number | null {
        return allPurchasableItems.value.find(i => i.id === item.id)?.stock ?? null
    }

    const cartItems = ref<Map<string, CartItem>>(new Map())
    const selectedDebitorProfile = ref<string | undefined>(shopOrder.debitorProfile?.id)

    shopOrder.items?.forEach((item: CartItem) => {
        const itemId = item.purchasableItem?.id
        if (!itemId || !item.amount) {
            return
        }
        const purchasableItem = allPurchasableItems.value.find(i => i.id === itemId) ?? item.purchasableItem
        const assignments = item.assignments ?? []
        if (
            !organizationId &&
            purchasableItem?.isUserAssignmentRequired &&
            item.amount > 0 &&
            assignments.length === 0
        ) {
            assignments.push({ position: 1, assignedName: shopOrder.debitorProfile?.name ?? "" })
        }
        cartItems.value.set(itemId, {
            purchasableItem,
            amount: item.amount,
            assignments,
            pricePerUnitInCents: item.pricePerUnitInCents,
        })
    })

    function productItems(product: Product): PurchasableItem[] {
        return product.variants?.length ? product.variants : [product as PurchasableItem]
    }

    function getItemQuantity(itemId: string): number {
        return cartItems.value.get(itemId)?.amount || 0
    }

    function getProductTotalQuantity(product: Product): number {
        return productItems(product).reduce((sum, item) => sum + getItemQuantity(item.id!), 0)
    }

    function setItemQuantity(item: PurchasableItem, quantity: number): void {
        if (quantity <= 0) {
            cartItems.value.delete(item.id!)
        } else {
            const existing = cartItems.value.get(item.id!)
            const assignments = (existing?.assignments ?? []).filter(a => (a.position ?? 0) <= quantity)
            cartItems.value.set(item.id!, { purchasableItem: item, amount: quantity, assignments })
        }
    }

    function getMaxOrderQuantity(product: Product, item?: PurchasableItem): number | undefined {
        if (product.isUserAssignmentRequired && !organizationId) {
            return 1
        }
        if (!item) {
            return product.maxOrderQuantity ?? undefined
        }
        const itemMax = item.maxOrderQuantity ?? undefined
        const stock = availableStock(item)
        const stockMax = stock !== null ? stock : undefined

        let max: number | undefined
        if (product.maxOrderQuantity == null) {
            max = itemMax
        } else {
            const productRemaining = Math.max(
                0,
                product.maxOrderQuantity - (getProductTotalQuantity(product) - getItemQuantity(item.id!)),
            )
            max = itemMax !== undefined ? Math.min(itemMax, productRemaining) : productRemaining
        }

        if (stockMax !== undefined) {
            max = max !== undefined ? Math.min(max, stockMax) : stockMax
        }

        return max
    }

    function getAssignment(itemId: string, position: number): CartItemAssignment | undefined {
        return cartItems.value.get(itemId)?.assignments?.find(a => a.position === position)
    }

    function setAssignment(itemId: string, position: number, value: string | Account): void {
        const cartItem = cartItems.value.get(itemId)
        if (!cartItem) {
            return
        }
        const assignments = cartItem.assignments ?? []
        const existing = assignments.find(a => a.position === position)
        const isAccount = typeof value !== "string"
        const patch = isAccount
            ? { account: value, assignedName: undefined }
            : { assignedName: value, account: undefined }
        if (existing) {
            Object.assign(existing, patch)
        } else {
            assignments.push({ position, ...patch })
        }
        cartItem.assignments = assignments
        cartItems.value.set(itemId, { ...cartItem })
    }

    function updateQuantity(product: Product, item: PurchasableItem, quantity: number): void {
        const max = getMaxOrderQuantity(product, item)
        const capped = max !== undefined ? Math.min(quantity, max) : quantity
        setItemQuantity(item, capped > 0 ? capped : 0)
        if (product.isUserAssignmentRequired && !organizationId && capped > 0) {
            setAssignment(item.id!, 1, shopOrder.debitorProfile?.name ?? "")
        }
    }

    function getAssignableMembers(product: Product): OrganizationMember[] {
        if (product.isFrameworkAgreementRequired) {
            return assignableMembers.value.filter(m => m.hasActiveFrameworkAgreement)
        }
        return assignableMembers.value
    }

    const total = computed(() => {
        let sum = 0
        cartItems.value.forEach(cartItem => {
            if (cartItem?.amount && cartItem.amount > 0) {
                const price = getBestPrice(cartItem.purchasableItem!, cartItem.amount)
                if (price !== null) {
                    sum += price * cartItem.amount
                }
            }
        })
        return sum
    })

    // The buyer picks the payment method here already, so the checkout can be opened right after ordering
    const allowedPaymentMethods = ref<PaymentMethod[]>([])
    const selectedPaymentMethod = ref<PaymentMethod | null>(null)
    let paymentMethodsRequestId = 0

    async function refreshPaymentMethods() {
        const requestId = ++paymentMethodsRequestId
        const { data } = await http.getBg<{ allowedPaymentMethods: PaymentMethod[] }>(
            `/order/shop/${shopConfigurationId}/${shopOrderId}/payment-methods`,
            {
                total: total.value,
                debitorProfile: selectedDebitorProfile.value,
            },
        )

        if (requestId !== paymentMethodsRequestId) {
            // A newer cart state is already being loaded
            return
        }

        allowedPaymentMethods.value = data.allowedPaymentMethods ?? []
        if (selectedPaymentMethod.value && !allowedPaymentMethods.value.includes(selectedPaymentMethod.value)) {
            selectedPaymentMethod.value = null
        }
    }

    if (shopOrder.shopConfiguration?.offersPaymentMethodSelection) {
        // Which methods are allowed depends on the order value and the invoice recipient
        watchDebounced([total, selectedDebitorProfile], () => refreshPaymentMethods(), {
            debounce: 300,
            maxWait: 1000,
        })

        await refreshPaymentMethods()
    }

    const canOrder = computed(() => {
        if (!selectedDebitorProfile.value || cartItems.value.size === 0) {
            return false
        }
        for (const product of products.value) {
            if (!product.isPurchasable) {
                return false
            }
            const productTotal = getProductTotalQuantity(product)
            if (productTotal > 0 && productTotal < (product.minOrderQuantity || 1)) {
                return false
            }
            if (!organizationId && product.isFrameworkAgreementRequired && !hasActiveFrameworkAgreement) {
                return false
            }
            if (product.isUserAssignmentRequired && !organizationId) {
                continue
            }
            if (product.isUserAssignmentRequired && product.isNominalRegistrationRequired) {
                const available = getAssignableMembers(product)
                if (productTotal > available.length) {
                    return false
                }
                const assignedIds = productItems(product).flatMap(i =>
                    (cartItems.value.get(i.id!)?.assignments ?? []).map(a => a.account?.id).filter(Boolean),
                )
                if (new Set(assignedIds).size !== assignedIds.length) {
                    return false
                }
            }
            for (const item of productItems(product)) {
                const cartItem = cartItems.value.get(item.id!)
                if (!cartItem?.amount || cartItem.amount === 0) {
                    continue
                }
                if (getBestPrice(item, cartItem.amount) === null) {
                    return false
                }
                if (product.isUserAssignmentRequired) {
                    for (let i = 1; i <= cartItem.amount; i++) {
                        const a = (cartItem.assignments ?? []).find(x => x.position === i)
                        if (!a) {
                            return false
                        }
                        if (product.isNominalRegistrationRequired ? !a.account?.id : !a.assignedName?.trim()) {
                            return false
                        }
                    }
                }
            }
        }
        return true
    })

    function buildPayload(): ShopOrderSubmitPayload {
        return {
            debitorProfile: selectedDebitorProfile.value,
            items: Array.from(cartItems.value.values())
                .filter(item => item.amount && item.amount > 0 && item.purchasableItem)
                .map(item => ({
                    purchasableItem: item.purchasableItem!.id,
                    amount: item.amount,
                    assignments: (item.assignments ?? []).map(a => ({
                        position: a.position,
                        account: a.account?.id ?? null,
                        assignedName: a.assignedName ?? null,
                    })),
                })),
        }
    }

    async function refreshProducts() {
        await refreshProductsData()
        for (const [itemId, cartItem] of cartItems.value) {
            const item = allPurchasableItems.value.find(i => i.id === itemId)
            if (item?.stock !== undefined && item.stock !== null && (cartItem.amount ?? 0) > item.stock) {
                setItemQuantity(item, item.stock)
            }
        }
    }

    return {
        shopOrder: shopOrderItem,
        products,
        productItems,
        debitorProfiles,
        availableStock,
        refreshProducts,
        customer: {
            notRegisteredForChampionship,
            noMembersFound,
            hasActiveFrameworkAgreement,
            assignableMembers,
            getAssignableMembers,
        },
        cart: {
            items: cartItems,
            total,
            selectedDebitorProfile,
            allowedPaymentMethods,
            selectedPaymentMethod,
            canOrder,
            getItemQuantity,
            getProductTotalQuantity,
            getBestPrice,
            getMaxOrderQuantity,
            getAssignment,
            setAssignment,
            updateQuantity,
            buildPayload,
        },
    }
}
